/**
 * ============================================================================
 * ÜRETİM KAYDININ AYNA ALANLARI — TEK OKUMA VE TEK YAZMA NOKTASI
 * ============================================================================
 *
 * ── NİÇİN BU DOSYA VAR ─────────────────────────────────────────────────────
 * `production/{id}` dokümanını üç ayrı yer yazıyor ve üçü FARKLI alan kümesi
 * yazıyordu:
 *
 *   · göç betiği            → orderName, customerName, customerShortName, orderTotal
 *   · sunucu (send.ts)      → customerName, itemSummary, lineItemCount   (orderName YOK)
 *   · tarayıcı (bu ekranlar)→ yalnızca sourceLabel                        (müşteri YOK)
 *
 * Okuyan taraf ise `customerShortName ?? customerName` gibi zincirler
 * kuruyordu. `??` yalnızca `null`/`undefined`'da devreye girer: sunucunun
 * yazdığı `customerName: ""` zinciri KIRMAZ, ekranda boşluk basılır ve kimse
 * hata görmez. Canlıda ölçüldü: 136 SSH üretim kaydının 136'sında müşteri adı,
 * 179 stok siparişi kaydının 179'unda kaynak etiketi hiç yoktu.
 *
 * ÇÖZÜM: hem okuma hem yazma tek modülden geçer.
 *   · Okumada `firstFilled` kullanılır — BOŞ DİZE de eksik sayılır.
 *   · Yazmada `productionDocForSsh` / `productionDocForStockOrder` aynı alan
 *     adlarını üretir; ekranın okuduğu alanla yazılan alan ayrışamaz.
 *
 * ── GÜVENLİK KURALLARI SINIRI ──────────────────────────────────────────────
 * `firestore.rules` §6 istemcinin `stockReductionDone`, `stockReductionKey`,
 * `stockReductionResult` ve `recipeOwnerId` yazmasını YASAKLAR — bunlar
 * sunucunun güvendiği alanlardır. Bu yüzden yazma yardımcıları o alanlara
 * DOKUNMAZ; `stock/consume.ts` zaten tanımsız değeri "düşülmemiş" sayar.
 * `priority` de kural gereği küçük harflidir (`low|normal|urgent`).
 */

import { serverTimestamp, type FieldValue } from "firebase/firestore";
import { tl } from "./format";

/** Üretim kaydının kaynak tipi — üç kaynak da aynı koleksiyonda durur. */
export type ProductionSourceType = "order" | "ssh" | "stockOrder";

/** Ekranların okuduğu ayna alanları. Hepsi opsiyoneldir: canlı veri karışıktır. */
export interface ProductionMirrorFields {
  sourceType?: ProductionSourceType;
  sourceId?: string;
  sourceLabel?: string;
  orderName?: string;
  customerName?: string;
  customerShortName?: string;
  channel?: string | null;
}

/**
 * İlk DOLU değeri döndürür. `??` ile farkı: boş dize de eksik sayılır.
 * Ayna alanları çoğu kayıtta `""` olarak yazıldığı için bu fark belirleyicidir.
 */
export function firstFilled(...values: Array<string | null | undefined>): string {
  for (const v of values) {
    const s = (v ?? "").trim();
    if (s !== "") return s;
  }
  return "";
}

/**
 * Müşteri adı — ATÖLYE TAM ADI GÖRÜR.
 *
 * ── NİÇİN SIRA DEĞİŞTİ ─────────────────────────────────────────────────────
 * Eskiden `customerShortName` önceleniyordu ("PII sınırı" gerekçesiyle) ve
 * üretim listesinde "Şule", "Ezgi", "İpek İrem" gibi TEK AD görünüyordu;
 * siparişte ise "Şule Gülseven", "Ezgi Ataş", "İpek İrem Akçay" yazıyor.
 * Atölye, aynı gün iki "Ezgi"nin işini ayırt edemiyordu.
 *
 * Canlı ölçüm (2026-08-14, 4.591 üretim kaydı):
 *   · `customerName` + `customerShortName` birlikte dolu : 4.212
 *   · yalnız `customerName` (SSH kayıtları)              :   138
 *   · yalnız `customerShortName`                         :     0  ← hiç yok
 *   · ikisi de boş                                       :   241
 * Yani kısa ad, tam adın olmadığı TEK BİR kayıtta bile kurtarıcı değil; tam ad
 * öncelenince hiçbir satır bilgi KAYBETMEZ, 4.350 satır bilgi KAZANIR.
 *
 * PII notu: bu değişiklik yeni bir alan YAZMAZ. `customerName` üretim
 * dokümanında zaten duruyor (`fields.ts → buildProductionDoc`); burada yalnızca
 * hangisinin gösterileceği değişir. Kısa ad yedek olarak korunur.
 * Hiçbiri yoksa BOŞ DİZE döner — çağıran yeri "—" ile doldurur.
 */
export function productionCustomer(p: ProductionMirrorFields): string {
  return firstFilled(p.customerName, p.customerShortName);
}

/**
 * Kaynak başlığı: sipariş numarası / SSH etiketi / stok siparişi etiketi.
 *
 * SSH ve stok siparişinde önce `sourceLabel` denenir; göç edilmiş kayıtlarda o
 * alan yok, o yüzden kimlikten üretilen etiket son çare olarak kalır.
 */
export function productionSourceTitle(p: ProductionMirrorFields): string {
  const id = p.sourceId ?? "";
  if (p.sourceType === "ssh") return firstFilled(p.sourceLabel, p.orderName) || `SSH-${id}`;
  if (p.sourceType === "stockOrder") return firstFilled(p.sourceLabel) || `Stok Siparişi-${id}`;
  return firstFilled(p.orderName, p.sourceLabel) || `Sipariş ${id}`;
}

/**
 * Liste satırının başlığı: müşteri adı varsa o, yoksa kaynak etiketi.
 * Eskiden `customerShortName ?? customerName ?? badgeText` yazılıyordu ve
 * `customerName: ""` yüzünden başlık BOŞ basılıyordu.
 */
export function productionHeadline(p: ProductionMirrorFields): string {
  return productionCustomer(p) || productionSourceTitle(p);
}

/**
 * Kaynak TÜRÜ — yalnızca "Sipariş / SSH / Stok siparişi".
 *
 * Başlık ile rozet aynı metni basınca (müşteri adı yoksa başlık da
 * `productionSourceTitle` olur → "Sipariş 77554" iki kez yan yana) rozet bu
 * kısa etikete düşer: renk ve tür bilgisi korunur, tekrar kalkar.
 */
export function productionSourceKind(p: ProductionMirrorFields): string {
  if (p.sourceType === "ssh") return "SSH";
  if (p.sourceType === "stockOrder") return "Stok siparişi";
  return "Sipariş";
}

/* ══════════════════════════════════════════════════════════════════════════
 * ÜRÜN ADINDAKİ ÖLÇÜ METNİ
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Ürün adından ölçü çıkarır: "Bely 3 Raflı Kitaplık - 180 x 38 cm" → 180 × 38.
 *
 * ── NİÇİN GEREKLİ ──────────────────────────────────────────────────────────
 * Kesim ekranındaki referans "Ölçü" sütunu `optionSize`/`variantTitle`
 * okuyordu. Canlı ölçüm (2026-08-14, 4.000 sipariş kalemi örneklemi):
 *   · `optionSize` dolu   : 0
 *   · `variantTitle` dolu : 0
 *   · ürün adında ölçü    : 3.316  (%83)
 * Yani ölçünün TEK kaynağı ürün adıdır. Bu bir TAHMİNDİR: çağıran taraf
 * sonucu "öneri" olarak işaretlemek ZORUNDADIR (bkz. `CuttingList.tsx`).
 *
 * Ayraç olarak `x`, `X`, `×` ve `*` kabul edilir; Türkçe ondalık (`12,5`) ve
 * nokta (`12.5`) tanınır. Ad içinde birden çok eşleşme varsa SONUNCUSU alınır
 * ("6 Raflı Kitaplık 80 x 30" gibi adlarda baştaki sayı ölçü değildir).
 * Eşleşme yoksa `null` döner — uydurma değer ÜRETİLMEZ.
 */
export interface ParsedSize {
  size1: number;
  size2: number;
  /** Ekranda gösterilecek metin, Türkçe ondalık ayracıyla: "180 x 38". */
  label: string;
}

const SIZE_PATTERN = /(\d{1,4}(?:[.,]\d{1,2})?)\s*[x×X*]\s*(\d{1,4}(?:[.,]\d{1,2})?)/g;

function trNumber(raw: string): number {
  return Number(raw.replace(",", "."));
}

/** Sayıyı Türkçe ondalık ayracıyla metne çevirir (18.5 → "18,5"). */
function trText(v: number): string {
  return String(v).replace(".", ",");
}

export function parseSizeFromTitle(title: string | null | undefined): ParsedSize | null {
  const text = (title ?? "").trim();
  if (!text) return null;

  SIZE_PATTERN.lastIndex = 0;
  let last: RegExpExecArray | null = null;
  for (let m = SIZE_PATTERN.exec(text); m !== null; m = SIZE_PATTERN.exec(text)) last = m;
  if (!last) return null;

  const size1 = trNumber(last[1]!);
  const size2 = trNumber(last[2]!);
  if (!Number.isFinite(size1) || !Number.isFinite(size2) || size1 <= 0 || size2 <= 0) return null;

  return { size1, size2, label: `${trText(size1)} x ${trText(size2)}` };
}

/**
 * Referans ölçü etiketi: önce veri alanları, sonra ürün adından çıkarım.
 * `firstFilled` kullanılır — `??` boş dizeyi yakalamaz ve canlıdaki alanların
 * TAMAMI boş dizedir.
 */
export function referenceSizeLabel(args: {
  optionSize?: string | null;
  variantTitle?: string | null;
  productTitle?: string | null;
}): { label: string; fromTitle: boolean } {
  const stored = firstFilled(args.optionSize, args.variantTitle);
  if (stored) return { label: stored, fromTitle: false };
  const parsed = parseSizeFromTitle(args.productTitle);
  return parsed ? { label: parsed.label, fromTitle: true } : { label: "", fromTitle: false };
}

/**
 * Varyant metnini ürün adının TEKRARI olduğunda eler.
 *
 * Canlıda `itemSummary` kalemlerinin bir kısmı varyantı adın İÇİNDE de taşıyor
 * ("Imra Vestiyer – 120 x 38 - Keten Ekru" + variantTitle "Keten Ekru"); ekran
 * ikisini `·` ile birleştirince "… Keten Ekru · Keten Ekru" çıkıyordu.
 * Karşılaştırma Türkçe küçük harfle yapılır ("İ/ı" tuzağı).
 */
export function distinctVariantText(
  productTitle: string | null | undefined,
  variantTitle: string | null | undefined,
): string {
  const v = (variantTitle ?? "").trim();
  if (!v) return "";
  const t = (productTitle ?? "").toLocaleLowerCase("tr");
  return t.includes(v.toLocaleLowerCase("tr")) ? "" : v;
}

/* ══════════════════════════════════════════════════════════════════════════
 * TUTAR SÜTUNU — TEK DAVRANIŞ
 * ══════════════════════════════════════════════════════════════════════════ */

export interface AmountDisplay {
  /** Ekrana basılacak metin. */
  text: string;
  /** Metin "—" ise NİÇİN bilinmediği; doluysa boş dize. */
  reason: string;
}

/**
 * Sipariş tutarı gösterimi.
 *
 * ── ESKİ DAVRANIŞ (canlıda ölçüldü) ────────────────────────────────────────
 * `p.orderTotal === undefined ? "—" : tl(p.orderTotal)` yazılıyordu. Sonuç üç
 * ayrı satır tipinde ÜÇ ayrı görüntü:
 *   · stok siparişi satırları (`orderTotal: 0`)      → "₺0,00"   (tutar yokmuş gibi değil)
 *   · SSH satırları (alan hiç yok, 136 kayıt)        → "—"       (gerekçesiz)
 *   · göç edilmiş sipariş kayıtları (56 kayıt)       → "—"       (gerekçesiz)
 * Atölye "₺0,00" ile "—" arasındaki farkın ne anlama geldiğini bilmiyordu.
 *
 * ── TEK KURAL ──────────────────────────────────────────────────────────────
 *   · sayı varsa           → biçimlendirilmiş tutar (0 ise "₺0,00")
 *   · sayı yoksa           → "—" ve NİÇİN bilinmediği `reason` alanında
 * `reason` çağıran tarafta `title` + ekran okuyucu metni olarak basılır.
 */
export function productionAmount(
  p: { sourceType?: ProductionSourceType; orderTotal?: number | null },
  opts?: { sourceMissing?: boolean },
): AmountDisplay {
  if (typeof p.orderTotal === "number" && Number.isFinite(p.orderTotal)) {
    return { text: tl(p.orderTotal), reason: "" };
  }
  if (p.sourceType === "ssh") {
    return { text: "—", reason: "SSH kaydı siparişten doğmadı; sipariş tutarı tutulmaz." };
  }
  if (p.sourceType === "stockOrder") {
    return { text: "—", reason: "Stok siparişinde sipariş tutarı yoktur." };
  }
  if (opts?.sourceMissing) {
    return { text: "—", reason: "Kaynak sipariş veritabanında yok; tutar okunamıyor." };
  }
  return {
    text: "—",
    reason: "Sipariş tutarı bu üretim kaydına kopyalanmamış (göç edilmiş eski kayıt).",
  };
}

/* ══════════════════════════════════════════════════════════════════════════
 * YAŞLANMA — KUYRUKTA BEKLEYEN İŞ
 * ══════════════════════════════════════════════════════════════════════════ */

/** Kayıt kaç GÜNDÜR bu kuyrukta? Tarih okunamazsa `null`. */
export function ageInDays(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  let d: Date | null = null;
  if (v instanceof Date) d = v;
  else if (typeof v === "object" && typeof (v as { toDate?: unknown }).toDate === "function") {
    d = (v as { toDate: () => Date }).toDate();
  }
  if (!d || Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

/**
 * Yaşlanma seviyesi. Eşikler atölyenin sözlü kuralından: 3 gün "geç kaldı",
 * 7 gün "kayıp iş". Canlıda aktif 31 kaydın 7'si 7 gün ve üzeri bekliyor ve
 * ekranda bunların hiçbir görsel farkı yoktu.
 */
export type AgeLevel = "fresh" | "warn" | "stale";

export function ageLevel(days: number | null): AgeLevel {
  if (days === null) return "fresh";
  if (days >= 7) return "stale";
  if (days >= 3) return "warn";
  return "fresh";
}

/* ══════════════════════════════════════════════════════════════════════════
 * KESİM SATIRI ALAN ADLARI
 * ══════════════════════════════════════════════════════════════════════════ */

/** `production/{id}/cuttingList/{lineId}` satırının ekranda okunan alanları. */
export interface CuttingLineFields {
  /** Kural + arayüz yazımı. */
  orderItemId?: string;
  /** Sunucunun eski yazımı. */
  lineItemId?: string;
  /** Göç betiğinin yazımı — canlıdaki 10 satırın TAMAMI yalnızca bunu taşıyor. */
  itemId?: string;
  /** `firestore.rules` beyaz listesinin ve bu ekranın adı. */
  productName?: string;
  /** Sunucunun `saveCuttingList` içinde yazdığı ad. */
  productTitle?: string;
}

/**
 * Kesim satırının bağlı olduğu sipariş kaleminin kimliği.
 *
 * ★ ÜÇ AD, TEK ANLAM. Yalnızca `orderItemId` okuyan ekran, göçle gelen
 * satırları GÖRMEZ; Firestore hata vermez, alan boş döner.
 * Sunucu karşılığı: `functions/src/production/fields.ts → readCuttingLineItemId`.
 */
export function cuttingLineItemId(line: CuttingLineFields): string {
  return firstFilled(line.orderItemId, line.lineItemId, line.itemId);
}

/**
 * Kesim satırının ürün adı.
 *
 * Sunucu `productTitle` yazıyordu, bu ekran `productName` okuyordu; üretim
 * detayındaki kesim tablosunun "Ürün" sütunu her satırda boş kalıyordu.
 * Sunucu artık iki adı da yazar; burası da iki adı da okur ki geçmiş satırlar
 * da görünsün.
 */
export function cuttingLineProductName(line: CuttingLineFields): string {
  return firstFilled(line.productName, line.productTitle);
}

/* ══════════════════════════════════════════════════════════════════════════
 * YAZMA — "Üretime Gönder" düğmelerinin ürettiği doküman
 * ══════════════════════════════════════════════════════════════════════════ */

/** Kural gereği geçerli öncelik değerleri (`firestore.rules` §6). */
export type ProductionPriority = "low" | "normal" | "urgent";

export function normalizePriority(raw: unknown): ProductionPriority {
  const s = String(raw ?? "").toLocaleLowerCase("tr");
  if (s === "urgent" || s === "acil") return "urgent";
  if (s === "low" || s === "düşük" || s === "dusuk") return "low";
  return "normal";
}

/** İki düğmenin de ürettiği ortak gövde. */
interface CommonInput {
  status: string;
  statusLabel?: string;
  uid: string;
  priority?: unknown;
  note?: string;
}

/**
 * ★ ALAN KÜMESİ SUNUCUYLA AYNI OLMALI.
 *
 * Sunucu tarafındaki karşılığı `functions/src/production/fields.ts →
 * buildProductionDoc`. Tarayıcı orada yazılan alanların bir kısmını YAZAMAZ
 * (`firestore.rules` §6 `stockReductionDone` / `stockReductionKey` /
 * `stockReductionResult` / `recipeOwnerId` yasak), ama YAZABİLDİĞİ her alanı
 * aynı adla ve aynı varsayılanla yazar. Aksi hâlde aynı ekran, kaydın hangi
 * yoldan açıldığına göre farklı davranır: `channel` yoksa kanal rozeti "Eski
 * sistem"e düşer, `fastShipping` yoksa hızlı gönderim işareti kaybolur,
 * `startedAt`/`completedAt` yoksa süre sütunu boş kalır.
 *
 * Yasak alanların yokluğu sorun değildir: `stock/consume.ts` tanımsız değeri
 * "düşülmemiş" sayar.
 */
function base(sourceType: ProductionSourceType, sourceId: string, input: CommonInput) {
  return {
    schemaVersion: 1,
    sourceType,
    sourceId,
    // Sipariş kaynaklı olmayan kayıtta boş dize — alanı hiç yaratmamak,
    // okuyan tarafı yine "yok" durumuna düşürür.
    sourceRef: "",
    orderId: "",
    status: input.status,
    statusLabel: input.statusLabel ?? "",
    priority: normalizePriority(input.priority),

    customerShortName: "",
    channel: null,
    orderTotal: 0,
    fastShipping: false,
    orderCreatedAt: null,

    cuttingLineCount: 0,
    statusHistoryCount: 0,
    startedAt: null,
    completedAt: null,
    leadTimeHours: 0,

    note: input.note ?? "",
    createdAt: serverTimestamp() as FieldValue,
    createdBy: input.uid,
    updatedAt: serverTimestamp() as FieldValue,
    updatedBy: input.uid,
    // Kuralın `notDeleted()` kapısı — yoksa kayıt bir daha güncellenemez.
    deleted: false,
  };
}

export interface SshMirrorSource {
  /** SSH kaydının kendi kimliği. */
  id: string;
  orderName?: string;
  customerName?: string;
  productName?: string;
  productTitle?: string;
  problemNote?: string;
  priority?: string;
  quantity?: number;
  volume?: number;
}

/**
 * SSH → üretim kaydı gövdesi.
 *
 * ★ Müşteri adı ve sipariş numarası SSH kaydından ÜRETİM KAYDINA KOPYALANIR.
 * Bu kopyalama daha önce hiç yapılmıyordu: üretim ekranında müşteri sütunu boş,
 * başlık ise "SSH-{doküman kimliği}" görünüyordu; atölye hangi siparişin işini
 * yaptığını göremiyordu. Alan adları `production/status.ts` ve `Production.tsx`
 * ile aynı — ayna alanı tek isimde buluşur.
 */
export function productionDocForSsh(row: SshMirrorSource, input: CommonInput) {
  const orderName = firstFilled(row.orderName);
  const title = firstFilled(row.productTitle, row.productName);
  const quantity = Number(row.quantity ?? row.volume ?? 1) || 1;

  return {
    ...base("ssh", row.id, { ...input, note: input.note ?? row.problemNote ?? "" }),
    // Etiket sipariş numarasını taşır; yoksa SSH kimliğine düşer.
    sourceLabel: orderName ? `SSH ${orderName}` : `SSH-${row.id}`,
    orderName,
    customerName: firstFilled(row.customerName),
    lineItemCount: 1,
    itemSummary: title ? [{ title, variantTitle: "", quantity, imageUrl: "" }] : [],
  };
}

export interface StockOrderMirrorSource {
  id: string;
  productName?: string;
  productTitle?: string;
  note?: string;
  priority?: string;
  quantity?: number;
  volume?: number;
}

/** Stok siparişi → üretim kaydı gövdesi. Etikette ÜRÜN ADI durur, kimlik değil. */
export function productionDocForStockOrder(row: StockOrderMirrorSource, input: CommonInput) {
  const title = firstFilled(row.productTitle, row.productName);
  const quantity = Number(row.quantity ?? row.volume ?? 1) || 1;

  return {
    ...base("stockOrder", row.id, { ...input, note: input.note ?? row.note ?? "" }),
    sourceLabel: title ? `Stok: ${title}` : `Stok Siparişi-${row.id}`,
    lineItemCount: 1,
    itemSummary: title ? [{ title, variantTitle: "", quantity, imageUrl: "" }] : [],
  };
}
